/**
 * Dragon Metalworks — image manifest (curated from your library)
 * ----------------------------------------------------------------
 * Files: public/images/  →  URLs: /images/... (via imagePath)
 *
 * Assignments are intentional: hero = strongest wide structural shot; about = yard
 * fabrication; projects = six distinct “hero” jobs; gallery = remaining strong shots
 * (no duplicates across those roles). Rename files anytime — update strings here only.
 */

export function imagePath(filename) {
  return encodeURI(`/images/${filename}`);
}

/** Wide, high-impact structural steel on site — blue sky, scale, premium feel */
export const HERO_IMAGE = imagePath('WhatsApp Image 2026-02-19 at 20.46.39.jpeg');

/** Fabrication yard: stacked gate panels — real workshop output */
export const ABOUT_IMAGE = imagePath('WhatsApp Image 2026-02-19 at 20.59.51 (5).jpeg');

/** Contact strip: interior beam grid, bright and structural */
export const CONTACT_VISUAL_IMAGE = imagePath('WhatsApp Image 2026-02-19 at 20.46.23.jpeg');

/**
 * Six featured jobs — each uses a different file from hero / about / gallery list.
 */
export const FEATURED_PROJECTS = [
  {
    name: 'Structural Steel Frame',
    cat: 'Primary framing',
    file: 'WhatsApp Image 2026-02-19 at 20.59.51 (4).jpeg',
    alt: 'Pitched structural steel roof frame on stone walls',
  },
  {
    name: 'Bespoke Balustrade',
    cat: 'Architectural steel',
    file: 'WhatsApp Image 2026-02-19 at 20.46.16.jpeg',
    alt: 'Grey structural steel frame for residential outdoor build',
  },
  {
    name: 'Custom Gates',
    cat: 'Gates',
    file: 'WhatsApp Image 2026-02-19 at 20.59.51 (6).jpeg',
    alt: 'Installed black ornamental steel pedestrian gate',
  },
  {
    name: 'Steel Staircase',
    cat: 'Staircases & roofs',
    file: 'WhatsApp Image 2026-02-19 at 20.46.31.jpeg',
    alt: 'Steel columns and beams with timber roof structure',
  },
  {
    name: 'Mezzanine Fabrication',
    cat: 'Mezzanines',
    file: 'WhatsApp Image 2026-02-19 at 20.59.50.jpeg',
    alt: 'Interior structural steel grid within building renovation',
  },
  {
    name: 'Site Installation',
    cat: 'On site',
    file: 'WhatsApp Image 2026-02-19 at 20.46.36 (1).jpeg',
    alt: 'Structural steel beams installed with site lifting equipment',
  },
].map((p) => ({ ...p, image: imagePath(p.file) }));

/**
 * Masonry gallery — does not repeat hero, about, or any featured project file above.
 */
export const SHOWCASE_GALLERY = [
  { file: 'WhatsApp Image 2026-02-19 at 20.59.51 (3).jpeg', alt: 'Steel roof trusses over masonry shell' },
  { file: 'WhatsApp Image 2026-02-19 at 20.46.33.jpeg', alt: 'Custom red steel roof frame above timber deck' },
  { file: 'WhatsApp Image 2026-02-19 at 20.46.36.jpeg', alt: 'Steel beams and columns with concrete plank floor' },
  { file: 'WhatsApp Image 2026-02-19 at 20.46.05 (2).jpeg', alt: 'Steel beam grid at sunset over blockwork' },
  { file: 'WhatsApp Image 2026-02-19 at 20.46.06 (1).jpeg', alt: 'Top-down view of structural steel layout' },
  { file: 'WhatsApp Image 2026-02-19 at 20.46.08 (1).jpeg', alt: 'Elevated view of red primed steel framework' },
  { file: 'WhatsApp Image 2026-02-19 at 20.46.07 (2).jpeg', alt: 'Steel floor grid with scaffolding at site' },
  { file: 'WhatsApp Image 2026-02-19 at 20.46.04 (1).jpeg', alt: 'Steel roof frame over timber joists' },
  { file: 'WhatsApp Image 2026-02-19 at 20.46.14.jpeg', alt: 'Residential extension steel frame in progress' },
  { file: 'WhatsApp Image 2026-02-19 at 20.46.22.jpeg', alt: 'Steel columns on foundation slab' },
  { file: 'WhatsApp Image 2026-02-19 at 20.46.27.jpeg', alt: 'Steel connection detail with site welding setup' },
  { file: 'WhatsApp Image 2026-02-19 at 20.46.29.jpeg', alt: 'Primed steel beams ready at building site' },
].map((item) => ({ ...item, src: imagePath(item.file) }));

/** Service card accents — match product type without repeating hero/about primary */
export const SERVICE_ACCENT_FILES = {
  'Steel Fabrication': 'WhatsApp Image 2026-02-19 at 20.59.51 (5).jpeg',
  'Structural Steel': 'WhatsApp Image 2026-02-19 at 20.46.29.jpeg',
  'Architectural Landscape Products': 'WhatsApp Image 2026-02-19 at 20.59.51 (6).jpeg',
  Staircases: 'WhatsApp Image 2026-02-19 at 20.46.31.jpeg',
  'Mobile Welding': 'WhatsApp Image 2026-02-19 at 20.46.27.jpeg',
  'Steel Supply': 'WhatsApp Image 2026-02-19 at 20.59.51 (4).jpeg',
  'Steel Beams': 'WhatsApp Image 2026-02-19 at 20.46.29.jpeg',
  'Steel Columns': 'WhatsApp Image 2026-02-19 at 20.46.23.jpeg',
  Design: 'WhatsApp Image 2026-02-19 at 20.46.33.jpeg',
  'Steel Connections': 'WhatsApp Image 2026-02-19 at 20.46.27.jpeg',
  'Steel Sections': 'WhatsApp Image 2026-02-19 at 20.59.51 (3).jpeg',
  'Site Surveys': 'WhatsApp Image 2026-02-19 at 20.46.39.jpeg',
  Gates: 'WhatsApp Image 2026-02-19 at 20.59.51 (6).jpeg',
  Balustrades: 'WhatsApp Image 2026-02-19 at 20.46.16.jpeg',
  Mezzanines: 'WhatsApp Image 2026-02-19 at 20.59.50.jpeg',
  Railing: 'WhatsApp Image 2026-02-19 at 20.46.14.jpeg',
  Welding: 'WhatsApp Image 2026-02-19 at 20.46.27.jpeg',
};

/** Try these in order if the requested asset fails to load */
export const IMAGE_FALLBACK_CHAIN = [
  'WhatsApp Image 2026-02-19 at 20.46.39.jpeg',
  'WhatsApp Image 2026-02-19 at 20.59.51 (4).jpeg',
  'WhatsApp Image 2026-02-19 at 20.59.51 (3).jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.33.jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.23.jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.36 (1).jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.04 (1).jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.05 (2).jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.06 (1).jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.08 (1).jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.10.jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.11.jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.14.jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.30.jpeg',
  'WhatsApp Image 2026-02-19 at 20.46.32.jpeg',
  'WhatsApp Image 2026-02-19 at 20.59.51 (1).jpeg',
  'WhatsApp Image 2026-02-19 at 20.59.51 (2).jpeg',
  'WhatsApp Image 2026-02-19 at 20.59.51 (7).jpeg',
  'WhatsApp Image 2026-02-19 at 20.59.51 (8).jpeg',
  'WhatsApp Image 2026-02-19 at 20.59.51 (9).jpeg',
  'WhatsApp Image 2026-02-19 at 20.59.51.jpeg',
].map(imagePath);
